/*
 * Used with a Report / Notebook to send it just to the current user in order to test the output.
 */
import React, {useState} from 'react';
import {
  Button,
  ButtonGroup,
  Classes,
  Dialog,
  DialogBody,
  FormGroup,
  InputGroup,
  Intent,
  Switch
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {IState} from '../../../store/reducers';
import {useCurrentUser} from '../../../hooks';
import {destinationIsChecked} from './utils';
import {DESTINATION} from './constants';

import './SendJustToMeDialog.scss';

interface SendJustToMeProps {
  destinations: string[] | undefined | null;
  show: boolean;
  onSend: (value: any) => void;
  onClose: (value?: any) => void;
}

interface SwitchInputProps {
  show: boolean;
  checked: boolean;
  defaultValue: string;
  label: string;
  helperText?: string;
  onChange: (value: string) => void;
  onCheckChange: (value: boolean) => void;
  /*
   * Only show switch if it makes sense (we have more than 1 destination available).
   * If there is only one destination, then we don't need to show the switch as we'll have to send something anyway.
   */
  showSwitch: boolean;
}

const SwitchInput = ({
  show,
  checked,
  defaultValue,
  label,
  helperText = '',
  onChange,
  onCheckChange,
  showSwitch,
}: SwitchInputProps) => {
  if (!show) {
    return null;
  }

  const _onCheckChange = (event: any) => {
    onCheckChange(event.target.checked);
  }

  const _onChange = (event: any) => {
    onChange(event.target.value);
  }

  return (
    <>
      {
        showSwitch && (
          <Switch
            label={label}
            defaultChecked={checked}
            onChange={_onCheckChange}
          />
        )
      }
      <FormGroup
        label={showSwitch ? "" : label}
        helperText={helperText || ''}
      >
        <InputGroup
          fill={true}
          defaultValue={defaultValue}
          onChange={_onChange}
          disabled={!checked}
        />
      </FormGroup>
    </>
  )
}

const SendJustToMeDialog = ({
  destinations: availableDestinations,
  show,
  onSend,
  onClose,
}: SendJustToMeProps) => {
  const _availableDestinations = availableDestinations || [];
  const currentUser = useCurrentUser();
  const [recipients, setRecipients] = useState<string>(currentUser?.email || '');
  const [channels, setChannels] = useState<string>('');

  const [destinations, setDestinations] = useState(_availableDestinations || []); // selected destinations

  const onDestinationChange = (type: DESTINATION) => (value: boolean) => {
    if (value) { // add
      setDestinations([...destinations, type]);
    } else { // remove
      setDestinations(destinations.filter((d: string) => d !== type));
    }
  };

  const _onSend = () => {
    onClose();

    const sendObj: any = {};

    [DESTINATION.EMAIL, DESTINATION.SLACK].forEach((_destination) => {
      if (destinationIsChecked(destinations, _destination)) {
        if (_destination === DESTINATION.EMAIL) {
          sendObj[_destination] = recipients;
        } else if (_destination === DESTINATION.SLACK) {
          sendObj[_destination] = channels.split(',').map((c: string) => c.trim());
        }
      } else if (destinationIsChecked(_availableDestinations || [], _destination)) {
        sendObj[_destination] = false; // do not send
      }
    });

    onSend(sendObj);
  }

  return (
    <Dialog
      title="Send Just to Me"
      className="send-just-to-me-dialog"
      autoFocus={true}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
      enforceFocus={true}
      usePortal={true}
      isOpen={show}
      onClose={onClose}
      icon={IconNames.SEND_MESSAGE}
    >
      <DialogBody>
        <SwitchInput
          show={destinationIsChecked(_availableDestinations, DESTINATION.EMAIL)}
          checked={destinationIsChecked(destinations, DESTINATION.EMAIL)}
          defaultValue={recipients}
          label="Email"
          helperText="Seperate multiple email recipients using a comma."
          onChange={setRecipients}
          onCheckChange={onDestinationChange(DESTINATION.EMAIL)}
          showSwitch={_availableDestinations.length > 1}
        />
        <SwitchInput
          show={destinationIsChecked(_availableDestinations, DESTINATION.SLACK)}
          checked={destinationIsChecked(destinations, DESTINATION.SLACK)}
          defaultValue={channels}
          label="Slack"
          onChange={setChannels}
          onCheckChange={onDestinationChange(DESTINATION.SLACK)}
          showSwitch={_availableDestinations.length > 1}
        />
      </DialogBody>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_MAIN_SECTION}>
        </div>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <ButtonGroup>
            <Button
              text="Close"
              onClick={onClose}
            />
            <Button
              intent={Intent.PRIMARY}
              text="Send"
              onClick={_onSend}
            />
          </ButtonGroup>
        </div>
      </div>
    </Dialog>
  );
}

export default SendJustToMeDialog;
